import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  PlusIcon,
  SearchIcon,
  CubeIcon,
  ExclamationCircleIcon,
  PencilIcon,
  TrashIcon,
  XIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  LocationMarkerIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  FolderIcon,
} from "@heroicons/react/outline";
import { inventoryAPI } from "../api/inventory";
import { projectsAPI } from "../api/projects";
import { useAuth } from "../context/AuthContext";
import MaterialForm from "../components/inventory/MaterialForm";
import LoadingSpinner from "../components/common/LoadingSpinner";
import toast from "react-hot-toast";

const InventoryPage = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [showLowStock, setShowLowStock] = useState(false);
  const [activeTab, setActiveTab] = useState("stock");
  const [showMaterialModal, setShowMaterialModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showConsumeModal, setShowConsumeModal] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [viewingMaterial, setViewingMaterial] = useState(null);
  const [expandedLocations, setExpandedLocations] = useState({});
  const queryClient = useQueryClient();

  // Fetch materials
  const { data: materialsData, isLoading: materialsLoading } = useQuery({
    queryKey: ["materials", searchTerm],
    queryFn: () =>
      inventoryAPI.getMaterials({ search: searchTerm || undefined }),
  });

  // Fetch stock
  const { data: stockData, isLoading: stockLoading } = useQuery({
    queryKey: ["stock", showLowStock],
    queryFn: () =>
      showLowStock ? inventoryAPI.getLowStock() : inventoryAPI.getStock(),
  });

  // Fetch locations
  const { data: locationsData, isLoading: locationsLoading } = useQuery({
    queryKey: ["locations"],
    queryFn: () => inventoryAPI.getLocations(),
  });

  // Fetch projects for transfer
  const { data: projectsData } = useQuery({
    queryKey: ["projects"],
    queryFn: () => projectsAPI.getAll(),
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => inventoryAPI.deleteMaterial(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["materials"]);
      queryClient.invalidateQueries(["stock"]);
      toast.success("Material deleted successfully");
    },
    onError: () => toast.error("Failed to delete material"),
  });

  // Transfer mutation
  const transferMutation = useMutation({
    mutationFn: (data) => inventoryAPI.transferStock(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["stock"]);
      toast.success("Stock transferred successfully");
      setShowTransferModal(false);
      setSelectedItem(null);
    },
    onError: () => toast.error("Failed to transfer stock"),
  });

  // Consume mutation
  const consumeMutation = useMutation({
    mutationFn: (data) => inventoryAPI.consumeStock(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["stock"]);
      toast.success("Stock consumed successfully");
      setShowConsumeModal(false);
      setSelectedItem(null);
    },
    onError: () => toast.error("Failed to consume stock"),
  });

  const materials = materialsData?.data || [];
  const stockItems = stockData?.data || [];
  const locations = locationsData?.data || [];
  const projects = projectsData?.data?.projects || [];

  const canManage =
    user?.role === "super_admin" ||
    user?.role === "project_manager" ||
    user?.role === "sub_manager";
  const canDelete = user?.role === "super_admin";

  const getStatus = (item) => {
    const available = item.quantity - item.reserved_quantity;
    if (available <= 0) return { label: "Out of Stock", color: "badge-danger" };
    if (available <= item.min_stock)
      return { label: "Low Stock", color: "badge-warning" };
    return { label: "In Stock", color: "badge-success" };
  };

  // Group stock items by location
  const groupStockByLocation = (items) => {
    const grouped = {};

    items.forEach((item) => {
      const locationId = item.location_id || "unassigned";
      if (!grouped[locationId]) {
        grouped[locationId] = {
          locationId: locationId,
          locationName: item.location_name || "Unknown Location",
          items: [],
        };
      }
      grouped[locationId].items.push(item);
    });

    return Object.values(grouped);
  };

  const groupedStock = groupStockByLocation(stockItems);

  // Toggle location expansion
  const toggleLocation = (locationId) => {
    setExpandedLocations((prev) => ({
      ...prev,
      [locationId]: !prev[locationId],
    }));
  };

  const isLocationExpanded = (locationId) => {
    return expandedLocations[locationId] !== undefined
      ? expandedLocations[locationId]
      : true;
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this material?")) {
      deleteMutation.mutate(id);
    }
  };

  const handleTransferSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = {
      material_id: selectedItem?.material_id,
      from_location_id: selectedItem?.location_id,
      to_location_id: parseInt(formData.get("to_location_id")),
      quantity: parseFloat(formData.get("quantity")),
      notes: formData.get("notes") || "",
    };
    transferMutation.mutate(data);
  };

  const handleConsumeSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = {
      material_id: selectedItem?.material_id,
      location_id: selectedItem?.location_id,
      quantity: parseFloat(formData.get("quantity")),
      project_id: parseInt(formData.get("project_id")) || null,
      notes: formData.get("notes") || "",
    };
    consumeMutation.mutate(data);
  };

  if (materialsLoading || stockLoading || locationsLoading)
    return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            📦 Inventory
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            Manage materials, stock levels, and locations
          </p>
        </div>
        {canManage && (
          <button
            onClick={() => setShowMaterialModal(true)}
            className="btn-primary flex items-center gap-2"
          >
            <PlusIcon className="h-5 w-5" />
            Add Material
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search materials..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <button
          onClick={() => setShowLowStock(!showLowStock)}
          className={`px-4 py-2 rounded-lg border transition-colors flex items-center gap-2 ${
            showLowStock
              ? "bg-primary-500 text-white border-primary-500"
              : "border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
          }`}
        >
          <ExclamationCircleIcon className="h-5 w-5" />
          Low Stock Only
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 dark:border-slate-700">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab("stock")}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "stock"
                ? "border-primary-500 text-primary-600 dark:text-primary-400"
                : "border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
            }`}
          >
            <CubeIcon className="h-4 w-4 inline mr-2" />
            Stock ({stockItems.length})
          </button>
          <button
            onClick={() => setActiveTab("locations")}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "locations"
                ? "border-primary-500 text-primary-600 dark:text-primary-400"
                : "border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
            }`}
          >
            <LocationMarkerIcon className="h-4 w-4 inline mr-2" />
            Locations ({locations.length})
          </button>
        </nav>
      </div>

      {/* Content */}
      <div className="mt-6">
        {activeTab === "stock" && (
          <>
            {stockItems.length === 0 ? (
              <div className="text-center py-12">
                <CubeIcon className="h-16 w-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                  No materials found
                </h3>
                <p className="text-slate-600 dark:text-slate-400">
                  Add your first material to start tracking inventory
                </p>
                {canManage && (
                  <button
                    onClick={() => setShowMaterialModal(true)}
                    className="btn-primary mt-4 inline-flex items-center gap-2"
                  >
                    <PlusIcon className="h-5 w-5" />
                    Add Material
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-8">
                {groupedStock.map((group) => {
                  const isExpanded = isLocationExpanded(group.locationId);

                  return (
                    <div key={group.locationId} className="space-y-3">
                      {/* Location Header */}
                      <div
                        className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/30 p-2 rounded-lg transition-colors"
                        onClick={() => toggleLocation(group.locationId)}
                      >
                        <button className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
                          {isExpanded ? (
                            <ChevronDownIcon className="h-4 w-4" />
                          ) : (
                            <ChevronRightIcon className="h-4 w-4" />
                          )}
                        </button>
                        <LocationMarkerIcon className="h-5 w-5 text-primary-500" />
                        <span className="font-semibold text-slate-900 dark:text-white">
                          {group.locationName}
                        </span>
                        <span className="text-sm text-slate-400">
                          ({group.items.length} items)
                        </span>
                      </div>

                      {/* Materials Grid */}
                      {isExpanded && (
                        <div className="pl-4 border-l-2 border-primary-200 dark:border-primary-800">
                          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                            {group.items.map((item, index) => {
                              const status = getStatus(item);
                              return (
                                <motion.div
                                  key={item.id}
                                  initial={{ opacity: 0, y: 20 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{ delay: index * 0.05 }}
                                  className="card hover:shadow-lg transition-all duration-300"
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex items-center gap-3">
                                      <div className="p-3 bg-primary-50 dark:bg-primary-900/20 rounded-xl">
                                        <CubeIcon className="h-6 w-6 text-primary-500" />
                                      </div>
                                      <div>
                                        <h3 className="font-semibold text-slate-900 dark:text-white">
                                          {item.material_name ||
                                            "Unknown Material"}
                                        </h3>
                                        <p className="text-sm text-slate-500 dark:text-slate-400">
                                          Code: {item.material_code || "N/A"}
                                        </p>
                                      </div>
                                    </div>
                                    <span className={`badge ${status.color}`}>
                                      {status.label}
                                    </span>
                                  </div>

                                  <div className="mt-4 grid grid-cols-2 gap-2">
                                    <div>
                                      <p className="text-sm text-slate-500 dark:text-slate-400">
                                        Available
                                      </p>
                                      <p className="text-xl font-bold text-slate-900 dark:text-white">
                                        {item.available_quantity ||
                                          item.quantity ||
                                          0}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-sm text-slate-500 dark:text-slate-400">
                                        Unit
                                      </p>
                                      <p className="text-xl font-bold text-slate-900 dark:text-white">
                                        {item.unit || "pcs"}
                                      </p>
                                    </div>
                                  </div>

                                  {canManage && (
                                    <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between">
                                      <div className="flex items-center gap-1">
                                        <button
                                          onClick={() => {
                                            setSelectedItem(item);
                                            setShowTransferModal(true);
                                          }}
                                          className="p-1.5 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                                          title="Transfer Stock"
                                        >
                                          <ArrowUpIcon className="h-4 w-4 text-blue-400" />
                                        </button>
                                        <button
                                          onClick={() => {
                                            setSelectedItem(item);
                                            setShowConsumeModal(true);
                                          }}
                                          className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                          title="Consume Stock"
                                        >
                                          <ArrowDownIcon className="h-4 w-4 text-red-400" />
                                        </button>
                                        <button
                                          onClick={() =>
                                            setEditingMaterial(item)
                                          }
                                          className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                                          title="Edit"
                                        >
                                          <PencilIcon className="h-4 w-4 text-slate-400" />
                                        </button>
                                        {canDelete && (
                                          <button
                                            onClick={() =>
                                              handleDelete(item.material_id)
                                            }
                                            className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                            title="Delete"
                                          >
                                            <TrashIcon className="h-4 w-4 text-red-400 hover:text-red-500" />
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </motion.div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {activeTab === "locations" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                📍 Inventory Locations
              </h3>
              {canManage && (
                <button
                  onClick={() => setShowLocationModal(true)}
                  className="btn-primary flex items-center gap-2"
                >
                  <PlusIcon className="h-4 w-4" />
                  Add Location
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {locations.map((location) => (
                <div
                  key={location.id}
                  className="card hover:shadow-lg transition-all duration-300"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
                        <LocationMarkerIcon className="h-5 w-5 text-primary-500" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900 dark:text-white">
                          {location.location_name}
                        </h4>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          {location.location_type}
                        </p>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          Stock Items: {location.stock_count || 0}
                        </p>
                      </div>
                    </div>
                    <span
                      className={`badge ${location.location_type === "warehouse" ? "badge-primary" : "badge-info"}`}
                    >
                      {location.location_type}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ============================================ */}
      {/* MODALS */}
      {/* ============================================ */}

      {/* Create Material Modal */}
      {showMaterialModal && (
        <MaterialForm
          onClose={() => setShowMaterialModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries(["materials"]);
            queryClient.invalidateQueries(["stock"]);
            setShowMaterialModal(false);
            toast.success("Material created successfully");
          }}
        />
      )}

      {/* Edit Material Modal */}
      {editingMaterial && (
        <MaterialForm
          material={editingMaterial}
          onClose={() => setEditingMaterial(null)}
          onSuccess={() => {
            queryClient.invalidateQueries(["materials"]);
            queryClient.invalidateQueries(["stock"]);
            setEditingMaterial(null);
            toast.success("Material updated successfully");
          }}
        />
      )}

      {/* Transfer Modal */}
      {showTransferModal && selectedItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl max-w-md w-full"
          >
            <div className="p-6 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                  Transfer Stock
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {selectedItem.material_name} - Available:{" "}
                  {selectedItem.available_quantity || selectedItem.quantity}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowTransferModal(false);
                  setSelectedItem(null);
                }}
                className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                <XIcon className="h-5 w-5 text-slate-500" />
              </button>
            </div>
            <form onSubmit={handleTransferSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  From Location
                </label>
                <input
                  type="text"
                  value={selectedItem.location_name}
                  disabled
                  className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  To Location
                </label>
                <select
                  name="to_location_id"
                  required
                  className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Select Location</option>
                  {locations
                    .filter((l) => l.id !== selectedItem.location_id)
                    .map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.location_name}
                      </option>
                    ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Quantity
                </label>
                <input
                  type="number"
                  name="quantity"
                  required
                  min="1"
                  max={selectedItem.available_quantity || selectedItem.quantity}
                  className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter quantity"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Notes
                </label>
                <input
                  type="text"
                  name="notes"
                  className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                  placeholder="Optional notes"
                />
              </div>
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
                <button
                  type="button"
                  onClick={() => {
                    setShowTransferModal(false);
                    setSelectedItem(null);
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Transfer
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Consume Modal */}
      {showConsumeModal && selectedItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl max-w-md w-full"
          >
            <div className="p-6 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                  Consume Stock
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {selectedItem.material_name} - Available:{" "}
                  {selectedItem.available_quantity || selectedItem.quantity}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowConsumeModal(false);
                  setSelectedItem(null);
                }}
                className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                <XIcon className="h-5 w-5 text-slate-500" />
              </button>
            </div>
            <form onSubmit={handleConsumeSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Location
                </label>
                <input
                  type="text"
                  value={selectedItem.location_name}
                  disabled
                  className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Quantity
                </label>
                <input
                  type="number"
                  name="quantity"
                  required
                  min="1"
                  max={selectedItem.available_quantity || selectedItem.quantity}
                  className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter quantity"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Project
                </label>
                <select
                  name="project_id"
                  className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Select Project (Optional)</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Notes
                </label>
                <input
                  type="text"
                  name="notes"
                  className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                  placeholder="Optional notes"
                />
              </div>
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
                <button
                  type="button"
                  onClick={() => {
                    setShowConsumeModal(false);
                    setSelectedItem(null);
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Consume
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default InventoryPage;
